import { supabase } from "@/infrastructure/supabase/client";
import type {
  BoardActivityRow,
  BoardFeatureDependencyRow,
  BoardFeatureRow,
  MilestoneRow,
  MvpComponentDependencyRow,
  MvpComponentRow,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

export interface CreateWorkstreamInput {
  key: string;
  code: string;
  title: string;
  // Nullable, not optional (`| null` over `?`, per the coding guidelines) — the
  // caller passes each explicitly, null for "unset".
  summary: string | null;
  target_milestone_key: string | null;
  start_date: string | null;
  end_date: string | null;
  sort_order: number;
}

export interface CreateBoardFeatureInput {
  title: string;
  description?: string | null;
  component_id?: string | null;
  workstream_key: string;
  milestone_key?: string | null;
  impl_kind?: BoardFeatureRow["impl_kind"];
  mvp_blocking?: boolean;
  acceptance_md?: string | null;
  notes?: string | null;
  delivery_status?: BoardFeatureRow["delivery_status"];
  sort_order?: number;
}

// PostgREST caps every response at 1000 rows, so a bare select silently drops the
// tail once a table outgrows it (board_features passed 1000). Page through with the
// caller's (stable) order until a short page comes back.
const PAGE_SIZE = 1000;

async function selectAllPages<T>(
  page: (from: number, to: number) => PromiseLike<{ data: unknown; error: unknown }>
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await page(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const batch = (data as T[] | null) ?? [];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) return rows;
  }
}

function buildUpdatePayload(patch: Partial<BoardFeatureRow>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch) as [
    keyof BoardFeatureRow,
    BoardFeatureRow[keyof BoardFeatureRow],
  ][]) {
    if (value !== undefined) out[key as string] = value;
  }
  return out;
}

export const boardRepository = {
  async listWorkstreams(): Promise<WorkstreamRow[]> {
    const { data, error } = await supabase
      .from("workstreams")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data as WorkstreamRow[] | null) ?? [];
  },

  // Create a workstream from the implementation timeline. Shares the same table
  // the board groups by, so a new workstream is immediately a board group too.
  // RLS ("authenticated write workstreams", migration 0119 — `for all`) gates the
  // insert to can_write users server-side.
  async createWorkstream(input: CreateWorkstreamInput): Promise<WorkstreamRow> {
    const { data, error } = await supabase
      .from("workstreams")
      .insert({
        key: input.key,
        code: input.code,
        title: input.title,
        summary: input.summary ?? null,
        target_milestone_key: input.target_milestone_key ?? null,
        start_date: input.start_date ?? null,
        end_date: input.end_date ?? null,
        sort_order: input.sort_order,
      })
      .select("*")
      .single();
    if (error) throw error;
    return data as WorkstreamRow;
  },

  // Update a workstream's editable fields from the timeline — name and/or Gantt
  // schedule (BF-198, BF-213). Keyed by the text PK `key`. RLS ("authenticated
  // write workstreams", migration 0119) gates this to can_write users — a
  // view-only user's write is rejected server-side.
  async updateWorkstream(
    key: string,
    patch: Partial<Pick<WorkstreamRow, "title" | "start_date" | "end_date">>
  ): Promise<WorkstreamRow> {
    const { data, error } = await supabase
      .from("workstreams")
      .update(patch)
      .eq("key", key)
      .select("*")
      .single();
    if (error) throw error;
    return data as WorkstreamRow;
  },

  async listMilestones(): Promise<MilestoneRow[]> {
    const { data, error } = await supabase
      .from("milestones")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data as MilestoneRow[] | null) ?? [];
  },

  // Update a milestone's target date from the timeline. Keyed by the text PK
  // `key`. RLS ("authenticated write milestones", migration 0119 — `for all`)
  // gates this to can_write users; a view-only user's write is rejected.
  async updateMilestone(
    key: string,
    patch: Pick<MilestoneRow, "target_date">
  ): Promise<MilestoneRow> {
    const { data, error } = await supabase
      .from("milestones")
      .update(patch)
      .eq("key", key)
      .select("*")
      .single();
    if (error) throw error;
    return data as MilestoneRow;
  },

  async listComponents(): Promise<MvpComponentRow[]> {
    const { data, error } = await supabase
      .from("mvp_components")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data as MvpComponentRow[] | null) ?? [];
  },

  async listDependencies(): Promise<MvpComponentDependencyRow[]> {
    const { data, error } = await supabase.from("mvp_component_dependencies").select("*");
    if (error) throw error;
    return (data as MvpComponentDependencyRow[] | null) ?? [];
  },

  async listFeatures(): Promise<BoardFeatureRow[]> {
    return selectAllPages<BoardFeatureRow>((from, to) =>
      supabase
        .from("board_features")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to)
    );
  },

  async featureById(id: string): Promise<BoardFeatureRow | null> {
    const { data, error } = await supabase
      .from("board_features")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return (data as BoardFeatureRow | null) ?? null;
  },

  async createFeature(input: CreateBoardFeatureInput): Promise<BoardFeatureRow> {
    const payload = {
      title: input.title,
      description: input.description ?? null,
      component_id: input.component_id ?? null,
      workstream_key: input.workstream_key,
      milestone_key: input.milestone_key ?? null,
      impl_kind: input.impl_kind ?? null,
      mvp_blocking: input.mvp_blocking ?? false,
      acceptance_md: input.acceptance_md ?? null,
      notes: input.notes ?? null,
      delivery_status: input.delivery_status ?? ("not-started" as const),
      ...(input.sort_order !== undefined ? { sort_order: input.sort_order } : {}),
    };
    const { data, error } = await supabase
      .from("board_features")
      .insert(payload)
      .select("*")
      .single();
    if (error) throw error;
    return data as BoardFeatureRow;
  },

  async updateFeature(id: string, patch: Partial<BoardFeatureRow>): Promise<BoardFeatureRow> {
    const { data, error } = await supabase
      .from("board_features")
      .update(buildUpdatePayload(patch))
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data as BoardFeatureRow;
  },

  async deleteFeature(id: string): Promise<void> {
    const { error } = await supabase.from("board_features").delete().eq("id", id);
    if (error) throw error;
  },

  async listFeatureDependencies(): Promise<BoardFeatureDependencyRow[]> {
    return selectAllPages<BoardFeatureDependencyRow>((from, to) =>
      supabase
        .from("board_feature_dependencies")
        .select("*")
        .order("id", { ascending: true })
        .range(from, to)
    );
  },

  async addFeatureDependencies(featureId: string, dependsOnIds: string[]): Promise<void> {
    if (dependsOnIds.length === 0) return;
    const rows = dependsOnIds.map((id) => ({
      feature_id: featureId,
      depends_on_feature_id: id,
    }));
    const { error } = await supabase
      .from("board_feature_dependencies")
      .upsert(rows, { onConflict: "feature_id,depends_on_feature_id", ignoreDuplicates: true });
    if (error) throw error;
  },

  async removeFeatureDependencies(featureId: string, dependsOnIds: string[]): Promise<void> {
    if (dependsOnIds.length === 0) return;
    const { error } = await supabase
      .from("board_feature_dependencies")
      .delete()
      .eq("feature_id", featureId)
      .in("depends_on_feature_id", dependsOnIds);
    if (error) throw error;
  },

  /**
   * One page of audit activity, newest first (migrations 0116/0117). Keyset
   * paginated on `seq` (monotonic, gap-tolerant) — pass the previous page's
   * `nextCursor` to fetch older rows. `featureId` scopes to one item's trail.
   */
  async listActivity(opts: {
    featureId?: string;
    cursor?: number | null;
    limit?: number;
  }): Promise<{ rows: BoardActivityRow[]; nextCursor: number | null }> {
    const limit = opts.limit ?? 50;
    let query = supabase
      .from("board_activity")
      .select("*")
      .order("seq", { ascending: false })
      .limit(limit);
    if (opts.featureId) query = query.eq("feature_id", opts.featureId);
    if (opts.cursor != null) query = query.lt("seq", opts.cursor);

    const { data, error } = await query;
    if (error) throw error;
    const rows = (data as BoardActivityRow[] | null) ?? [];
    // A full page implies there may be more; the last row's seq is the cursor.
    const nextCursor = rows.length === limit ? (rows[rows.length - 1]?.seq ?? null) : null;
    return { rows, nextCursor };
  },

  subscribeToChanges(onChange: () => void) {
    const channel = supabase
      .channel("board-features-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "board_features" }, onChange)
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
